import type { EchoAttack } from '../../../data/full/babacool';
import { angleOf } from '../../../shared/angle';
import { fan } from '../../attacks/fan';
import type { AttackModule } from '../../attacks/types';
import { pullTrigger } from '../../attacks/trigger';
import { keepWhere, wholeTicks } from '../../effects';
import type { PlayerState, PropState } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { inHalfTime } from '../skills/half-time';
import { flightOver, flyThrough, ownerOf } from '../../props';

export const NOTE = 'note';

export const echo: AttackModule<EchoAttack> = {
  reads: [
    'attackCooldownMul',
    'damageMul',
    'echoCountAdd',
    'echoPowerMul',
    'pierceAdd',
    'projectileCountAdd',
    'projectileSpeedMul',
  ],

  step({ state }, player, attack, input) {
    if (!pullTrigger(player, input.fire, attack.cooldownTicks)) {
      return;
    }
    const count = Math.max(1, Math.floor(statValue(player, 'projectileCountAdd', 1)));
    const speed = statValue(player, 'projectileSpeedMul', attack.speed);
    const damage = statValue(player, 'damageMul', attack.damage);
    const hits = statValue(player, 'pierceAdd', attack.pierce) + 1;
    for (const way of fan(player.aim, count, attack.spreadRadians)) {
      const note: PropState = {
        id: state.nextEntityId,
        kind: NOTE,
        playerId: player.id,
        phase: 'held',
        x: player.x,
        y: player.y,
        prevX: player.x,
        prevY: player.y,
        vx: way.x * speed,
        vy: way.y * speed,
        damage,
        ticksLeft: 1,
        hitsLeft: hits,
        rank: 0,
      };
      state.nextEntityId += 1;
      (state.props ??= []).push(note);
    }
    state.events.push({
      type: 'playerFired',
      playerId: player.id,
      x: player.x,
      y: player.y,
      angle: angleOf(player.aim),
    });
  },

  update(ctx) {
    const props = ctx.state.props;
    if (!props?.some((prop) => prop.kind === NOTE)) {
      return;
    }
    ctx.enemyGrid.rebuild(ctx.state.enemies);
    let spent = false;
    const known = props.length;
    for (let index = 0; index < known; index += 1) {
      const prop = props[index];
      if (prop?.kind !== NOTE) {
        continue;
      }
      if (prop.phase === 'held') {
        prop.ticksLeft -= 1;
        if (prop.ticksLeft > 0) {
          continue;
        }
        setOff(ctx, prop);
      }
      spent = fly(ctx, prop) || spent;
    }
    if (spent) {
      keepWhere(props, (prop) => prop.kind !== NOTE || prop.hitsLeft > 0);
      if (props.length === 0) {
        delete ctx.state.props;
      }
    }
  },

  reach(attack, player) {
    return statValue(player, 'projectileSpeedMul', attack.speed) * attack.rangeTicks;
  },
};

function echoOf(ctx: Pick<StepContext, 'content'>, owner: PlayerState): EchoAttack {
  const attack = ctx.content.classes.get(owner.classId)?.attack;
  if (attack?.kind !== 'echo') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" plays no echo`);
  }
  return attack;
}

export function echoShare(attack: EchoAttack, rank: number, power = 1): number {
  if (rank <= 0) {
    return 1;
  }
  const given = attack.echoes.length;
  const last = attack.echoes[given - 1] ?? 1;
  let share = attack.echoes[rank - 1] ?? last;
  for (let past = given; past < rank; past += 1) {
    share *= attack.fade;
  }
  return Math.min(1, share * power);
}

function echoCount(ctx: StepContext, owner: PlayerState, at: PropState): number {
  const attack = echoOf(ctx, owner);
  const count = Math.floor(statValue(owner, 'echoCountAdd', attack.echoes.length));
  return inHalfTime(ctx, owner, at) ? count + 1 : count;
}

function setOff(ctx: StepContext, prop: PropState): void {
  prop.phase = 'flying';
  prop.ticksLeft = echoOf(ctx, ownerOf(ctx.state, prop)).rangeTicks;
  prop.prevX = prop.x;
  prop.prevY = prop.y;
  queueEcho(ctx, prop);
}

function queueEcho(ctx: StepContext, from: PropState): void {
  const { state, tempo } = ctx;
  const owner = ownerOf(state, from);
  const rank = (from.rank ?? 0) + 1;
  if (rank > echoCount(ctx, owner, from)) {
    return;
  }
  const attack = echoOf(ctx, owner);
  const note = statValue(owner, 'damageMul', attack.damage);
  const power = statValue(owner, 'echoPowerMul', 1);
  const next: PropState = {
    id: state.nextEntityId,
    kind: NOTE,
    playerId: owner.id,
    phase: 'held',
    x: from.x,
    y: from.y,
    prevX: from.x,
    prevY: from.y,
    vx: from.vx,
    vy: from.vy,
    damage: note * echoShare(attack, rank, power),
    ticksLeft: Math.max(1, wholeTicks(attack.delayBeats * tempo.ticksPerBeat)),
    hitsLeft: statValue(owner, 'pierceAdd', attack.pierce) + 1,
    rank,
  };
  state.nextEntityId += 1;
  (state.props ??= []).push(next);
}

function fly(ctx: StepContext, prop: PropState): boolean {
  const owner = ownerOf(ctx.state, prop);
  const attack = echoOf(ctx, owner);
  if (flyThrough(ctx, prop, owner, attack.radius)) {
    return true;
  }
  if (flightOver(ctx, prop, attack.radius)) {
    prop.hitsLeft = 0;
    return true;
  }
  return false;
}
